import { format, Locale } from 'date-fns';
import { noop } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';

import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { useFormatDate } from '~/i18n/hooks/useFormatDate/useFormatDate';
import { useICUDateParsing } from '~/i18n/hooks/useFormatDate/useICUDateParsing';

import { DateFormat } from '../../../types/DateFormat';
import { InputWidth } from '../../../types/InputWidth';
import { getDateTimeFormatPattern } from '../../../utils/getDateTimeFormatPattern';
import { TextInput } from '../../TextInput/TextInput';

interface Props {
  name: string;
  value: Date | null;
  locale?: Locale;
  format?: DateFormat;
  placeholder?: string;
  width?: InputWidth;
  disabled?: boolean;
  error?: string;
  id?: string;
  min?: Date;
  max?: Date;
  onChange?: (date: Date | null) => void;
  onMonthNavigate?: (date: Date) => void;
}

export const InputTrigger = (props: Props) => {
  const hasLocalization = useFlag(FlagType.LanguageSelect);
  const formatDate = useFormatDate();
  const { parseICUDate } = useICUDateParsing();

  const {
    locale,
    placeholder,
    value,
    format: formatProps,
    onChange,
    onMonthNavigate,
    disabled,
    error,
    min,
    max,
    ...safeProps
  } = props;

  const [validationError, setValidationError] = useState<string | undefined>(
    undefined,
  );
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);

  // Configurable debounce delay (ms)
  const DEBOUNCE_DELAY = 1000;

  const deprecated_formatDateFn = () => {
    if (!value) return '';
    if (formatProps) {
      return formatDate(value, formatProps);
    }

    return format(value, getDateTimeFormatPattern(locale?.code), {
      locale,
    });
  };

  const formatDateFn = (date: Date | null) => {
    if (!date) return '';

    return hasLocalization
      ? formatDate(date, 'input_date')
      : format(date, getDateTimeFormatPattern(locale?.code), {
          locale,
        });
  };

  const [inputText, setInputText] = useState(formatDateFn(value));

  // Debounced validation function
  const validateDate = (text: string) => {
    if (!text.trim()) {
      onChange?.(null);
      return;
    }

    const parsedDate = parseICUDate(text);

    if (parsedDate) {
      let finalDate = parsedDate;

      // Clamp to min/max bounds
      if (min && parsedDate < min) {
        finalDate = min;
      } else if (max && parsedDate > max) {
        finalDate = max;
      }

      const currentValue = value?.getTime();
      const newValue = finalDate.getTime();

      if (currentValue !== newValue) {
        onChange?.(finalDate);
      }

      // change the Calendar view to show the month of the date
      onMonthNavigate?.(finalDate);
      setValidationError(undefined);
    } else if (text.trim().length > 2) {
      // Show error with the expected format based on ICU
      const expectedFormat = getDateTimeFormatPattern(
        locale?.code,
      ).toLowerCase();
      setValidationError(`Invalid date. Try format: ${expectedFormat}`);
    }
  };

  useEffect(() => {
    setInputText(formatDateFn(value));
    setValidationError(undefined);
  }, [value]);

  useEffect(() => {
    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, []);

  const handleInputChange = (newText: string | null) => {
    const text = newText || '';
    setInputText(text);

    setValidationError(undefined);

    // Clear existing debounce timeout
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    if (!text.trim()) {
      onChange?.(null);
      return;
    }

    debounceTimeout.current = setTimeout(() => {
      validateDate(text);
    }, DEBOUNCE_DELAY);
  };

  const handleBlur = () => {
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
      debounceTimeout.current = null;
    }

    validateDate(inputText);
  };

  const formatString = getDateTimeFormatPattern(locale?.code);
  const displayError = validationError || error;
  const inputDateText = deprecated_formatDateFn();
  return (
    <TextInput
      disabled={disabled}
      error={displayError}
      placeholder={placeholder ?? formatString.toLowerCase()}
      value={hasLocalization ? inputText : inputDateText}
      onChange={hasLocalization ? handleInputChange : noop}
      onBlur={hasLocalization ? handleBlur : undefined}
      {...safeProps}
    />
  );
};
